import { createFileRoute, useLocation, useNavigate, useRouter } from '@tanstack/react-router';
import { MessagesSquare, Sparkles } from 'lucide-react';
import { useEffect } from 'react';
import { useMe } from '~/api/auth';
import { useMarkOrderSeen } from '~/api/notifications';
import { OrderPage, type OrderTab } from '~/features/orders/OrderPage';
import { OrderRoom, type RoomPlace } from '~/features/orders/room/OrderRoom';
import type { RoomView } from '~/features/orders/room/useRoomActions';
import { Hint } from '~/ui/Hint';

interface Search {
  /** The classic page's side tab; in the chat view, notes and history open those views. */
  tab?: OrderTab;
  /** The stage open in full in the chat view (with view=stage). */
  stage?: string;
  /** A full view open over the chat view. */
  view?: RoomView;
  /** The chat view: the order page's chat, expanded. Its back goes to the order page. */
  layout?: 'chat';
}

const TABS: OrderTab[] = ['chat', 'notes', 'history'];
const VIEWS: RoomView[] = ['stage', 'notes', 'history', 'details'];

export const Route = createFileRoute('/_app/orders/$spaceId/$orderId')({
  validateSearch: (s: Record<string, unknown>): Search => {
    const stage = typeof s['stage'] === 'string' && s['stage'] ? s['stage'] : undefined;
    const view = (VIEWS as string[]).includes(String(s['view']))
      ? (s['view'] as RoomView)
      : undefined;
    return {
      tab: (TABS as string[]).includes(String(s['tab'])) ? (s['tab'] as OrderTab) : undefined,
      stage,
      view,
      // A stage or a full view only exists in the chat view, so old links to one still open it.
      layout: s['layout'] === 'chat' || stage || view ? 'chat' : undefined,
    };
  },
  component: OrderRoute,
});

/**
 * One order with a link of its own that a notification can open: the
 * classic page (every stage at once, the chat in a corner), or the chat view
 * (chat first, the pipeline on the left).
 */
function OrderRoute() {
  const { spaceId, orderId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/orders/$spaceId/$orderId' });
  const router = useRouter();
  const me = useMe();
  const tz = me.person.tz ?? me.workspace.tz;
  // Opening the order is seeing what it notified about.
  const { mutate: markSeen } = useMarkOrderSeen();
  useEffect(() => {
    markSeen(orderId);
  }, [orderId, markSeen]);
  // The chat view sits one step inside the order page, so it remembers whether it was opened from it.
  const fromOrder = useLocation({ select: (l) => l.state.fromOrder === true });
  const openChatView = () =>
    void navigate({ search: { layout: 'chat' }, state: { fromOrder: true } });
  // Back to the order page: the step back when it opened the chat view, else in its place.
  const backToOrder = () => {
    if (fromOrder) router.history.back();
    else void navigate({ search: {}, replace: true });
  };

  if (search.layout !== 'chat') {
    return (
      <div className="tracker -mx-4 -mt-6 min-h-(--main-h) px-4 pt-6 pb-28 md:-mx-8 md:px-8">
        <div className="mx-auto mb-3 flex max-w-[1240px] justify-end">
          <Hint label="The order as one conversation, with its stages beside it" side="bottom">
            <button
              type="button"
              onClick={openChatView}
              className="group inline-flex items-center gap-2 rounded-full bg-accent py-1.5 pr-1.5 pl-3.5 text-sm font-medium text-on-accent shadow-layer ring-4 ring-accent-soft transition-[background-color,box-shadow,scale] hover:bg-accent-ink hover:ring-accent/25 active:scale-[0.98] motion-reduce:transition-none"
            >
              <MessagesSquare size={15} aria-hidden />
              Chat view
              <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[10.5px] font-semibold tracking-wide uppercase">
                <Sparkles
                  size={11}
                  aria-hidden
                  className="transition-transform group-hover:rotate-12"
                />
                New
              </span>
            </button>
          </Hint>
        </div>
        <OrderPage
          key={orderId}
          orderId={orderId}
          spaceId={spaceId}
          tab={search.tab ?? 'notes'}
          onTab={(t) =>
            void navigate({
              search: { tab: t === 'notes' ? undefined : t },
              replace: true,
            })
          }
          tz={tz}
          onOpenRoom={openChatView}
        />
      </div>
    );
  }

  // Old links asked for the notes or the history tab; the chat view opens those views.
  const view: RoomView | null =
    search.view ?? (search.tab === 'notes' || search.tab === 'history' ? search.tab : null);
  const place: RoomPlace = { stage: search.stage ?? null, view };

  return (
    // Full height beside the sidebar on a computer; the whole screen on a phone, like a chat app.
    <div className="tracker fixed inset-0 z-45 overflow-hidden md:static md:z-auto md:-mx-8 md:-mt-6 md:-mb-8 md:h-(--main-h)">
      <OrderRoom
        key={orderId}
        orderId={orderId}
        spaceId={spaceId}
        tz={tz}
        place={place}
        onPlace={(p) =>
          void navigate({
            search: { layout: 'chat', stage: p.stage ?? undefined, view: p.view ?? undefined },
            state: true,
            replace: true,
          })
        }
        onBack={backToOrder}
      />
    </div>
  );
}
